<template>
	<div class="swiper" :class="{'swiper--visible-arrows': props.visibleArrows, 'swiper--full-width-spaced': props.fullWidthSpaced, 'swiper--loading': isLoadingInitialSlide}">
		<div class="swiper-container" ref="scrollElement" v-scroll.self="refreshVisibleSlides"
			 :class="{'h-full': props.fill}"
			 :style="{'grid-auto-columns': slidesPerView ? `${100 / slidesPerView}%` : undefined}">
			<slot/>
		</div>

		<client-only>
			<template v-if="props.showArrows">
				<btn class="swiper-button-prev" :class="{'is-disabled': isFirstSlideVisible && !props.loop}" :aria-label="$t('action.back')" icon :disabled="isFirstSlideVisible && !props.loop"
					 @click="slidePrev">
					<Icon :icon="icons.back" x-large force-size color="gray-400"/>
				</btn>
				<btn class="swiper-button-next" :class="{'is-disabled': isLastSlideVisible && !props.loop}" :aria-label="$t('action.next')" icon :disabled="isLastSlideVisible && !props.loop"
					 @click="slideNext">
					<Icon :icon="icons.next" x-large force-size color="gray-400"/>
				</btn>
			</template>

			<div v-if="props.showPagination" class="flex items-center justify-center mb-16 mt-8">
				<div v-for="(slide, slideViewIndex) in slides" @click="scrollToIndex(slideViewIndex)" :key="'slide-pagination-' + slideViewIndex"
					 class="w-10 h-10 mx-4 cursor-pointer rounded-full" :class="activeSlideIndex === slideViewIndex ? 'bg-gray-400' : 'bg-gray-300'"/>
			</div>
		</client-only>

	</div>
</template>
<script setup lang="ts">
import {sliderItemInjection} from "../../types/keys";
import {provide, ref, watch, computed, onMounted, onBeforeUnmount, nextTick} from "vue";
import type {Ref} from "vue";
import {isElementVisible} from "@spoferan/spoferan-ts-core";
import {backIcon, nextIcon} from "../../config";

const props = withDefaults(defineProps<{
	// The value of the active item(s) to display
	modelValue?: string | number,

	// Whether navigation dots shall be displayed
	showPagination?: boolean,

	// Whether navigation arrows shall be displayed
	showArrows?: boolean,

	// Whether navigation arrows shall always be visible
	// Otherwise they are only visible on hover for desktop devices
	visibleArrows?: boolean,

	// Whether a padding on the slides shall be applied to prevent the shadow from being cut off and to enable a full width view on mobile devices
	fullWidthSpaced?: boolean,

	// The ms after which the slides shall automatically change
	nextAfter?: number | boolean,

	// Whether to start the slider from the beginning when the end is reached
	loop?: boolean,

	// Whether enable navigation via keyboard
	withKeyboardNavigation?: boolean,

	// Number of slides per view (slides visible at the same time on slider's container).
	// Defaults to an auto view depending on the slide's content width.
	slidesPerView?: number,

	// If true, then active slide will be centered, not always on the left side.
	// TODO: implement when needed
	centered?: boolean,

	fill?: boolean,

}>(), {
	space: 0
});

const emit = defineEmits(['update:modelValue', 'input']);

const scrollElement: Ref<HTMLElement|null> = ref(null);
const slides = ref([]);

const activeSlideValue: Ref<string|number|undefined> = ref(props.modelValue);
const initialIndex = activeSlideValue.value ? slides.value.findIndex((slide) => slide.value === activeSlideValue.value) : 0;

// The index of the first slide that is currently visible
const activeSlideIndex = ref(initialIndex > -1 ? initialIndex : 0);

// The index of the slides that are currently visible
const visibleSlideIndex = ref([]);

const isLoadingInitialSlide = ref(props.modelValue !== undefined);

let shouldAutoChange: boolean = props.nextAfter
let autoSlideInterval = null;

watch(activeSlideIndex, (newIndex) => {
	if (slides.value.length > newIndex) {
		activeSlideValue.value = slides.value[newIndex].value;
	}
});
watch(activeSlideValue, (newValue) => {
	emit('update:modelValue', newValue);
});

watch(() => props.modelValue, (newValue) => {
	if (newValue !== activeSlideValue.value) {
		scrollToValue(newValue, false); // TODO: Fix
	}
});

const icons = {
	back: backIcon,
	next: nextIcon,
};

const isFirstSlideVisible = computed(() => {
	return activeSlideIndex.value === 0;
});

const isLastSlideVisible = computed(() => {
	return visibleSlideIndex.value[visibleSlideIndex.value.length - 1] === slides.value.length - 1;
});

function addSlide(value: string | number | undefined, element) {
	slides.value.push({
		value: value ?? (slides.value.length + 1),
		element: element
	});

	refreshVisibleSlides();
}

function slidePrev(byUser: boolean = true) {
	// Prevent auto sliding when manually changed slides
	if (byUser) {
		shouldAutoChange = false;
	}

	if (isFirstSlideVisible.value) {
		if (props.loop) {
			scrollToSlide(slides.value[slides.value.length - 1])
		}
		return;
	}

	scrollToSlide(slides.value[activeSlideIndex.value - 1])
}

function slideNext(byUser: boolean = true) {
	// Prevent auto sliding when manually changed slides
	if (byUser) {
		shouldAutoChange = false;
	}

	if (isLastSlideVisible.value) {
		if (props.loop) {
			scrollToSlide(slides.value[0])
		}

		return;
	}

	scrollToSlide(slides.value[activeSlideIndex.value + 1])
}

function scrollToIndex(index) {
	if (slides.value.length > index) {
		scrollToSlide(slides.value[index])
	}
}

function scrollToValue(value, smooth: boolean = true) {
	const slide = slides.value.find((slide) => slide.value === value);
	if (slide) {
		scrollToSlide(slide, smooth);
	}
}

function scrollToSlide(slide, smooth: boolean = true) {
	if (!slide?.element || !scrollElement.value) {
		return;
	}

	const currentScrollLeft = scrollElement.value!.scrollLeft;
	const scrollRect = scrollElement.value!.getBoundingClientRect();
	const slideRect = slide.element.getBoundingClientRect();

	// We use scrollTo instead of scrollIntoView as the latter does not support simultaneous scrolling when multiple sliders are present on a page, e.g., participation classes index page
	scrollElement.value!.scrollTo({
		left: slideRect.left - scrollRect.left + currentScrollLeft,
		behavior: smooth ? "smooth" : "instant",
	});
}

function refreshVisibleSlides() {
	if (!scrollElement.value) {
		return;
	}

	const scrollRect = scrollElement.value!.getBoundingClientRect();

	// Refresh the current active slide according to the scroll position
	const visibleSlides = slides.value.filter(slide => {
		const slideRect = slide.element.getBoundingClientRect();

		// Check if slide rect is fully visible
		return slideRect.left >= (scrollRect.left - 1) && slideRect.right <= (scrollRect.right + 1); // +-1 for a little tolerance as the last slide would never be fully visible
	});

	visibleSlideIndex.value = visibleSlides.map(slide => slides.value.indexOf(slide));

	activeSlideIndex.value = visibleSlideIndex.value[0];
}

function handleKeyDown(event) {
	if (!props.withKeyboardNavigation) {
		return;
	}

	if (event.key === 'ArrowLeft') {
		slidePrev();
	} else if (event.key === 'ArrowRight') {
		slideNext();
	}
}

provide(sliderItemInjection, {
	addSlide,
});

onMounted(() => {

	// Initially display the specified model value slide
 	nextTick(() => {
		if (props.modelValue !== undefined) {
			setTimeout(() => {
				scrollToValue(props.modelValue, false);
				isLoadingInitialSlide.value = false;
			}, 500); // Wait until all slides are fully loaded and we know the scroll positions to scroll to
		}
	});

	if (props.nextAfter) {
		// noinspection PointlessBooleanExpressionJS
		autoSlideInterval = setInterval(() => {

			// We only auto scroll to the next slide when the slider is fully visible to prevent auto scrolling when the user is not looking at the slider
			if (shouldAutoChange && scrollElement.value && isElementVisible(scrollElement.value!)) {
				slideNext(false);
			}
		}, props.nextAfter === true ? 1000 : (props.nextAfter as number));
	}

	if (props.withKeyboardNavigation) {
		window.addEventListener('keydown', handleKeyDown);
	}
});

onBeforeUnmount(() => {
	if (autoSlideInterval) {
		clearInterval(autoSlideInterval);
	}
	window.removeEventListener('keydown', handleKeyDown);
});

</script>

<style>
.swiper {
  width: 100%;
  position: relative;
}
.swiper .swiper-container {
  display: grid;
  grid-auto-flow: column;
  scroll-snap-type: x mandatory;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.swiper .swiper-container::-webkit-scrollbar {
  display: none;
}
.swiper .swiper-container .swiper-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: start;
  position: relative;
  z-index: 1;
}
.swiper.swiper--loading .swiper-container {
  opacity: 0;
}
.swiper.swiper--full-width-spaced .swiper-container {
  gap: 8px;
}
.swiper.swiper--full-width-spaced .swiper-container .swiper-slide {
  padding-left: 4px;
}
.swiper.swiper--full-width-spaced .swiper-container .swiper-slide:last-child {
  padding-right: 4px;
}
@media (max-width: 767px) {
  .swiper.swiper--full-width-spaced .swiper-container {
    gap: 0;
  }
  .swiper.swiper--full-width-spaced .swiper-container .swiper-slide {
    padding-left: 8px;
  }
  .swiper.swiper--full-width-spaced .swiper-container .swiper-slide:first-child {
    padding-left: 16px;
  }
  .swiper.swiper--full-width-spaced .swiper-container .swiper-slide:last-child {
    padding-right: 16px;
  }
}
.swiper:hover .btn.swiper-button-prev,
.swiper:hover .btn.swiper-button-next, .swiper.swiper--visible-arrows .btn.swiper-button-prev,
.swiper.swiper--visible-arrows .btn.swiper-button-next {
  opacity: 0.95;
}
.swiper .btn.swiper-button-prev,
.swiper .btn.swiper-button-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  opacity: 0;
}
.swiper .btn.swiper-button-prev.is-disabled,
.swiper .btn.swiper-button-next.is-disabled {
  display: none;
}
@media (max-width: 767px) {
  .swiper .btn.swiper-button-prev,
  .swiper .btn.swiper-button-next {
    opacity: 0.95;
  }
}
.swiper .btn.swiper-button-prev {
  left: 8px;
  right: auto;
}
.swiper .btn.swiper-button-next {
  right: 8px;
  left: auto;
}
</style>